import React, { useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Step1 from "./ShipmentFields";
import useMutation from "src/hooks/useMutation";
import useAuth from "src/hooks/useAuth";
import useNotification from "src/hooks/notification";
import { UPDATE_SHIPMENT_MUTATION } from "src/constants/graphql/mutations";
import useQueryParams from "src/hooks/useQueryParams";
import ConfirmUnitWeightModal from "./ConfirmUnitWeightModal";
import DraftShipmentModal from "./DraftShipmentModal";
import { serverFormatSeconds } from "src/utils/dates";
import { extractBillingTypes } from "src/utils/billingHelpers";
import useHasEnabledIntegration from "src/hooks/useHasEnabledIntegration";

const getShipmentSchema = (display, shipment) => {
  let validate = {};
  if (display.total_receiving_units) {
    validate.totalPieces = Yup.array()
      .of(
        Yup.object().shape({
          shipment_type: Yup.string().required("Required"),
          received: Yup.mixed().required("Required"),
        })
      )
      .min(1, "Required")
      .required("Required");
  }
  if (display.customer_po_number) {
    validate.customerPoNumber = Yup.string()
      .nullable()
      .required("Required");
  }
  if (display.carrier_name) {
    validate.carrierName = Yup.string()
      .nullable()
      .required("Required");
  }
  if (display.vendor_name) {
    validate.vendorName = Yup.string()
      .nullable()
      .required("Required");
  }
  if (display.order_number) {
    validate.orderNumber = Yup.string()
      .nullable()
      .required("Required");
  }
  if (display.bol) {
    validate.bol = Yup.string()
      .nullable()
      .required("Required");
  }
  if (display.total_weight) {
    validate.totalWeight = Yup.string()
      .nullable()
      .required("Required");
    validate.weightUnits = Yup.string()
      .nullable()
      .required("Required");
  }
  if (display.tracking_number) {
    validate.trackingNumber = Yup.string()
      .nullable()
      .required("Required");
  }
  if (display.truck_number) {
    validate.truckNumber = Yup.string()
      .nullable()
      .required("Required");
  }
  // Only require costCenterId if cost_center display is enabled AND the project has cost centers configured
  // This matches the display logic in ShipmentFields.js which only shows the field when both conditions are met
  const hasCostCenters = (shipment?.project?.costCenters?.length || 0) > 0;
  if (display.cost_center && hasCostCenters) {
    validate.costCenterId = Yup.string()
      .nullable()
      .required("Required");
  }

  const minimumShipmentImages = shipment?.project?.minimumShipmentImages || 0;
  if (minimumShipmentImages > 0) {
    const imageCount = shipment?.attachments?.length || 0;
    if (imageCount < minimumShipmentImages) {
      const message =
        minimumShipmentImages === 1
          ? "Minimum 1 image required"
          : `Minimum ${minimumShipmentImages} images required`;
      validate.shipmentImage = Yup.string()
        .nullable()
        .required(message);
    }
  }

  // Billing type is required for non-draft shipments only if project has inbound billing types
  if (!shipment?.draft) {
    const inboundBilling = shipment?.project?.billingTypes?.inbound;
    const { types: inboundBillingTypes } = extractBillingTypes(
      inboundBilling,
      shipment?.project?.billingType
    );

    if (inboundBillingTypes.length > 0) {
      validate.billingType = Yup.string()
        .nullable()
        .required("Required");
    }
  }

  return validate;
};

export default function Shipment({
  shipment,
  project,
  initialValues,
  onClose,
  formikRef,
  actionsRef,
  verticalLayout = false,
}) {
  const params = useQueryParams();
  const id = params.get("id");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifyError } = useNotification();

  // Use the parent project query as the source of truth for required field settings.
  const display = project?.fieldSettings ?? user?.company?.fieldSettings ?? {};
  const loading = !project?.id;
  const { hasIntegration, loading: loadingIntegration } = useHasEnabledIntegration(project);
  const schema = Yup.object().shape(getShipmentSchema(display, shipment));
  const [updateShipment] = useMutation(UPDATE_SHIPMENT_MUTATION);
  const [confirmState, setConfirmState] = useState(false);
  const isDraft = shipment.draft;
  const isOnsiteInventory = shipment.shipmentStatus === 'onsite_inventory';
  const isEditingExistingShipment = params.get("editShipment") === "true";
  const isExistingDraft = isDraft && isEditingExistingShipment;
  const [showDraftModal, setShowDraftModal] = useState(isExistingDraft);

  const executeShipmentUpdate = async (variables, setErrors) => {
    let mutationErrors = null;
    const response = await updateShipment({
      variables,
      onFailure: errors => {
        mutationErrors = errors;
        setErrors(errors);
      },
    });

    const updatedShipment = response?.data?.updateShipment?.shipment;
    if (mutationErrors || !updatedShipment) {
      notifyError(mutationErrors || "Failed to save shipment details");
      return null;
    }

    return updatedShipment;
  };

  const handleSave = async (
    { weightUnit, weightUnits, totalWeight, movewareDiaryAction, ...input },
    { setErrors },
    { skipNavigation = false } = {}
  ) => {
    // Only include weightUnits if it's actually provided (not hardcoded)
    const weight = weightUnits === "Kgs" ? totalWeight * 2.20462 : totalWeight;
    const shipmentInput = {
      ...input,
      draft: shipment.draft,
      totalWeight: weight,
    };

    // Only include weightUnits if it was provided by the user
    if (weightUnits) {
      shipmentInput.weightUnits = weightUnits === "Kgs" ? "Lbs" : weightUnits; // Convert Kgs to Lbs for storage
    }

    const variables = {
      id,
      input: shipmentInput,
    };
    const updatedShipment = await executeShipmentUpdate(variables, setErrors);
    if (!updatedShipment) return false;
    if (skipNavigation) return true;

    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.set("step", "items");
    navigate({ pathname, search: params.toString() });
    return true;
  };

  const handleMarkAsReceived = async setErrors => {
    const variables = {
      id,
      input: {
        ...initialValues,
        startTime: moment(new Date()).format(serverFormatSeconds),
        draft: false,
      },
    };
    const updatedShipment = await executeShipmentUpdate(variables, setErrors);
    if (!updatedShipment) return;

    params.set("shipmentId", updatedShipment.shipmentId);
    navigate({ search: params.toString() });
    setShowDraftModal(false);
  };

  const handleKeepAsDraft = () => {
    setShowDraftModal(false);
  };

  const handleConfirm = (values, formikHelpers) => {
    // Commented out weight unit confirmation modal
    // if (!shipment.completeTime && !isDraft && !confirmState && display.total_weight) {
    //   setConfirmState({ weightUnits: values.weightUnits });
    //   return Promise.resolve();
    // }

    setConfirmState(null);
    return handleSave(values, formikHelpers);
  };

  if (actionsRef) {
    actionsRef.current = {
      saveProgress: () =>
        handleSave(formikRef.current?.values || {}, {
          setErrors: formikRef.current?.setErrors || (() => {}),
        }, {
          skipNavigation: true,
        }),
    };
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={40} thickness={4} />
      </Box>
    );
  }

  return (
    <>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        validationSchema={(isDraft || isOnsiteInventory) ? null : schema}
        initialValues={{ ...initialValues, movewareDiaryAction: initialValues.movewareDiaryAction || "" }}
        onSubmit={handleConfirm}
      >
        <Form style={{ height: "100%" }}>
          <Box height="100%" display="flex" flexDirection="column">
            <Step1
              id={id}
              shipment={shipment}
              project={project}
              display={display}
              onClose={onClose}
              verticalLayout={verticalLayout}
              hasIntegration={hasIntegration}
              loadingIntegration={loadingIntegration}
            />
            {/* Commented out weight unit confirmation modal for now */}
            {/* {confirmState && (
              <ConfirmUnitWeightModal
                weightUnit={confirmState.weightUnits}
                onClose={() => setConfirmState(null)}
              />
            )} */}
          </Box>
        </Form>
      </Formik>
      {showDraftModal && (
        <DraftShipmentModal
          description="Has this shipment been received, or do you want to continue working on it as a draft?"
          onClose={() => setShowDraftModal(false)}
          onKeepAsDraft={handleKeepAsDraft}
          onMarkAsReceived={() => handleMarkAsReceived(() => {})}
        />
      )}
    </>
  );
}
